import {
    ArrowDownTrayIcon,
    CalendarIcon,
    CheckCircleIcon,
    CurrencyDollarIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    UserIcon,
    XCircleIcon
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { ADMIN_CONFIG } from "../../config/adminConfig";
import { useAdmin } from "../../contexts/AdminContext";
import { adminService } from "../../services/api";

const UserManagement = () => {
    const { refreshUsers, refreshKey } = useAdmin();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [roleFilter, setRoleFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [userStats, setUserStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        newThisMonth: 0,
    });

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const filters = {
                page: currentPage,
                limit: ADMIN_CONFIG.pagination.defaultLimit,
                search: searchTerm,
                role: roleFilter,
                status: statusFilter,
            };

            const [usersResponse, statsResponse] = await Promise.all([
                adminService.getAllUsers(filters),
                adminService.getUserStats(),
            ]);

            if (usersResponse.data) {
                setUsers(usersResponse.data.users || []);
                setTotalPages(usersResponse.data.pagination?.pages || 1);
            }

            if (statsResponse.data) {
                setUserStats(statsResponse.data);
            }
        } catch (error) {
            console.error("Failed to fetch users:", error);
            // Reset users to empty array on error
            setUsers([]);
            setUserStats({
                total: 0,
                active: 0,
                inactive: 0,
                newThisMonth: 0,
            });
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, roleFilter, statusFilter]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, refreshKey]);

    const handleStatusUpdate = async (userId, newStatus) => {
        try {
            await adminService.updateUserStatus(userId, newStatus);
            await refreshUsers();
            fetchUsers();
        } catch (error) {
            console.error("Failed to update user status:", error);
            alert("Failed to update user status");
        }
    };

    if (loading && users.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-200 border-t-green-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h2 className="text-4xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                    User Management
                </h2>
                <p className="text-gray-600 text-lg font-medium mt-2">Manage users and their account details</p>
            </div>

            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-white to-blue-50 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                    <div className="p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                                    <UserIcon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                    Total Users
                                </p>
                                <p className="text-3xl font-bold text-gray-900">{userStats.total}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-white to-green-50 backdrop-blur-sm border border-green-100 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                    <div className="p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="p-3 bg-gradient-to-r from-green-500 to-[#008000] rounded-xl">
                                    <CheckCircleIcon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                    Active Users
                                </p>
                                <p className="text-3xl font-bold text-gray-900">{userStats.active}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-white to-red-50 backdrop-blur-sm border border-red-100 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                    <div className="p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-xl">
                                    <XCircleIcon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                    Inactive Users
                                </p>
                                <p className="text-3xl font-bold text-gray-900">{userStats.inactive}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-white to-purple-50 backdrop-blur-sm border border-purple-100 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                    <div className="p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
                                    <CalendarIcon className="h-6 w-6 text-white" />
                                </div>
                            </div>
                            <div className="ml-4 flex-1">
                                <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                                    New This Month
                                </p>
                                <p className="text-3xl font-bold text-gray-900">{userStats.newThisMonth}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                        <FunnelIcon className="h-5 w-5 mr-2 text-[#008000]" />
                        Search & Filter Users
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search users by name or email..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                        />
                    </div>

                    <select
                        value={roleFilter}
                        onChange={e => setRoleFilter(e.target.value)}
                        className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                    >
                        <option value="">All Roles</option>
                        <option value="customer">Customer</option>
                        <option value="admin">Admin</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-white/70 backdrop-blur-sm"
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                        <option value="blocked">Blocked</option>
                    </select>
                </div>
            </div>

            {/* Users Table - Desktop */}
            <div className="hidden lg:block bg-gradient-to-br from-white to-gray-50 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">User</th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Orders
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Total Spent
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Joined
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white/80 backdrop-blur-sm divide-y divide-gray-200">
                            {users.map((user) => (
                                <tr
                                    key={user._id}
                                    className="hover:bg-green-50/50 transition-colors"
                                >
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-12 w-12 flex-shrink-0">
                                                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                                                    <span className="text-white font-bold text-lg">
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-bold text-gray-900">{user.name}</div>
                                                <div className="text-sm text-gray-600 font-medium">
                                                    {user.email}
                                                </div>
                                                {user.phone && (
                                                    <div className="text-xs text-gray-500 font-medium">
                                                        {user.phone}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <span
                                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border ${user.role === "admin"
                                                ? "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border-purple-200"
                                                : "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200"
                                                }`}
                                        >
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <select
                                            value={
                                                user.isBlocked ? "blocked" : user.isActive ? "active" : "inactive"
                                            }
                                            onChange={e => handleStatusUpdate(user._id, e.target.value)}
                                            className={`text-xs font-bold rounded-full px-3 py-1 border-none outline-none transition-colors ${user.isBlocked
                                                ? "bg-gradient-to-r from-red-100 to-pink-100 text-red-800"
                                                : user.isActive
                                                    ? "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000]"
                                                    : "bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800"
                                                }`}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="blocked">Blocked</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-900 font-medium">
                                            <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                                            {user.orders?.length || 0} orders
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="flex items-center text-sm font-bold text-[#008000]">
                                            <CurrencyDollarIcon className="h-4 w-4 text-green-500 mr-1" />₹
                                            {user.totalSpent || 0}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="flex items-center text-sm text-gray-600 font-medium">
                                            <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-3">
                                            {/* <button
                                                    onClick={() => {
                                                        console.log("View user details:", user);
                                                    }}
                                                    className="p-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:shadow-lg transition-shadow"
                                                    title="View Details"
                                                >
                                                    <EyeIcon className="h-4 w-4" />
                                                </button> */}
                                            <button
                                                onClick={() => {
                                                    const userData = {
                                                        name: user.name,
                                                        email: user.email,
                                                        phone: user.phone,
                                                        role: user.role,
                                                        status: user.status,
                                                        joinDate: user.createdAt,
                                                        orders: user.orders?.length || 0,
                                                        totalSpent: user.totalSpent || 0,
                                                    };
                                                    const dataStr = JSON.stringify(userData, null, 2);
                                                    const dataBlob = new Blob([dataStr], {
                                                        type: "application/json",
                                                    });
                                                    const url = URL.createObjectURL(dataBlob);
                                                    const link = document.createElement("a");
                                                    link.href = url;
                                                    link.download = `user_${user.name.replace(
                                                        /\s+/g,
                                                        "_"
                                                    )}_data.json`;
                                                    link.click();
                                                }}
                                                className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-shadow"
                                                title="Export User Data"
                                            >
                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 flex items-center justify-between border-t border-gray-200">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-bold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-bold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Page <span className="font-bold text-[#008000]">{currentPage}</span> of{" "}
                                    <span className="font-bold text-[#008000]">{totalPages}</span>
                                </p>
                            </div>
                            <div className="flex space-x-2">
                                {[...Array(Math.min(ADMIN_CONFIG.pagination.maxVisiblePages, totalPages))].map((_, i) => {
                                    const pageNum = i + 1;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${pageNum === currentPage
                                                ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Users Cards - Mobile */}
            <div className="lg:hidden space-y-4">
                {users.map((user) => (
                    <div
                        key={user._id}
                        className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-4 hover:shadow-xl transition-shadow"
                    >
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg">
                                    <span className="text-white font-bold text-lg">
                                        {user.name.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-lg font-bold text-gray-900 truncate">{user.name}</div>
                                    <div className="text-sm text-gray-600 truncate">{user.email}</div>
                                    {user.phone && <div className="text-xs text-gray-500">{user.phone}</div>}
                                </div>
                            </div>
                            <div className="flex flex-col items-end space-y-1">
                                <span
                                    className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${user.role === "admin"
                                        ? "bg-purple-100 text-purple-800"
                                        : "bg-blue-100 text-blue-800"
                                        }`}
                                >
                                    {user.role}
                                </span>
                                <div className="text-xs text-gray-500">
                                    {new Date(user.createdAt).toLocaleDateString("en-US", {
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-blue-50 rounded-xl p-3 text-center">
                                <div className="text-lg font-bold text-blue-600">{user.orders?.length || 0}</div>
                                <div className="text-xs text-blue-600 font-medium">Orders</div>
                            </div>
                            <div className="bg-green-50 rounded-xl p-3 text-center">
                                <div className="text-lg font-bold text-[#008000]">₹{user.totalSpent || 0}</div>
                                <div className="text-xs text-[#008000] font-medium">Total Spent</div>
                            </div>
                        </div>

                        {/* Status and Actions */}
                        <div className="flex items-center justify-between">
                            <div className="flex-1 mr-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    value={user.isBlocked ? "blocked" : user.isActive ? "active" : "inactive"}
                                    onChange={e => handleStatusUpdate(user._id, e.target.value)}
                                    className={`w-full text-sm font-medium rounded-xl px-3 py-2 border-0 outline-none transition-colors ${user.isBlocked
                                        ? "bg-red-100 text-red-800"
                                        : user.isActive
                                            ? "bg-green-100 text-[#008000]"
                                            : "bg-gray-100 text-gray-800"
                                        }`}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="blocked">Blocked</option>
                                </select>
                            </div>
                            <div className="flex space-x-2">
                                {/* <button
                                        onClick={() => {
                                            console.log("View user details:", user);
                                        }}
                                        className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                                        title="View Details"
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                    </button> */}
                                <button
                                    onClick={() => {
                                        const userData = {
                                            name: user.name,
                                            email: user.email,
                                            phone: user.phone,
                                            role: user.role,
                                            status: user.status,
                                            joinDate: user.createdAt,
                                            orders: user.orders?.length || 0,
                                            totalSpent: user.totalSpent || 0,
                                        };
                                        const dataStr = JSON.stringify(userData, null, 2);
                                        const dataBlob = new Blob([dataStr], { type: "application/json" });
                                        const url = URL.createObjectURL(dataBlob);
                                        const link = document.createElement("a");
                                        link.href = url;
                                        link.download = `user_${user.name.replace(/\s+/g, "_")}_data.json`;
                                        link.click();
                                    }}
                                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-2 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                                    title="Export Data"
                                >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default UserManagement;
